import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchProsAssessment } from "../../../store/slices/prosAssessmentsSlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function ProsFormPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentProsAssessment: d, isLoading, error } = useSelector((state) => state.prosAssessments);

  useEffect(() => {
    if (id) dispatch(fetchProsAssessment(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (d) {
      const timer = setTimeout(() => window.print(), 1000);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(timer);
        window.onafterprint = null;
      };
    }
  }, [d, navigate]);

  if (isLoading) return <div className="p-8 text-center">Loading Assessment...</div>;
  if (error) return <div className="p-8 text-danger text-center">{error}</div>;
  if (!d) return <div className="p-8 text-center">No record found.</div>;

  const fmtDate = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    }) : "________________";

  // Helper to get nested area data
  const getArea = (key) => d.part1_areas?.find(a => a.area_key === key) || {};

  return (
    <>
      <style jsx global>{`
                body {
                    font-family: "Arial", sans-serif !important;
                    background-color: white !important;
                    padding: 40px 60px !important;
                    color: #000 !important;
                }
                .logo { position: absolute; top: 20px; left: 40px; }
                .logo img { height: 60px !important; width: auto !important; }

                .assessment-container { max-width: 900px !important; margin: auto !important; }
                h2 { text-align: center !important; font-size: 20px !important; margin-bottom: 30px !important; font-weight: bold; text-decoration: underline; }
                
                .header-row { display: flex !important; justify-content: space-between !important; margin-bottom: 20px !important; }
                .underline { display: inline-block !important; border-bottom: 1px solid black !important; min-width: 200px !important; margin-left: 8px !important; font-weight: bold; }
                
                h3 { margin-top: 25px !important; font-size: 17px !important; font-weight: bold; border-bottom: 1px solid #000; padding-bottom: 5px; }
                .instruction { font-size: 12px !important; margin-bottom: 15px !important; font-style: italic; color: #444; }

                .section { border: 1px solid black !important; padding: 15px !important; margin-bottom: 20px !important; background-color: #fff !important; page-break-inside: avoid; }
                .section-title { font-weight: bold !important; font-size: 15px !important; margin-bottom: 8px !important; text-transform: uppercase; }
                .question { font-size: 12px !important; line-height: 1.4 !important; margin-bottom: 10px !important; font-style: italic; color: #555; }
                .answer-text { min-height: 50px; font-size: 13px; margin-bottom: 15px; white-space: pre-wrap; padding: 5px; border-radius: 4px; }

                .work-row { display: flex !important; align-items: center !important; gap: 20px !important; font-size: 12px !important; font-weight: bold; border-top: 1px dashed #ccc; padding-top: 10px; }
                .signature-line { display: flex !important; justify-content: space-between !important; margin-top: 50px !important; font-size: 14px !important; }
                .sig-box { border-top: 1px solid #000; width: 250px; text-align: center; padding-top: 5px; }

                .footer-brand { margin-top: 40px; border-top: 1px solid #000; padding-top: 10px; text-align: center; font-size: 10px; }
            `}</style>

      <div className="logo"><img src={logo} alt="Logo" /></div>

      <div className="assessment-container">
        <br /><br />
        <h2>PROS Comprehensive Psychiatric Rehabilitation Assessment</h2>

        <div className="header-row">
          <div><label>Name:</label><span className="underline">{d.client_name}</span></div>
          <div><label>Date:</label><span className="underline">{fmtDate(d.created_at)}</span></div>
        </div>

        <h3>Part 1. Exploring Areas for Change</h3>
        <p className="instruction">Try to provide some information about each area below. Questions stimulation thinking.</p>

        {/* Part 1 Sections */}
        {[
          { title: "Tell me about YOU!", q: "How would you describe yourself? What are your hopes and dreams?", key: "tellMeAboutYou" },
          { title: "Daily Routine", q: "How do you spend your days? Are you satisfied?", key: "dailyRoutine" },
          { title: "Employment", q: "Are you currently working? Would you like to work?", key: "employment" },
          { title: "Learning", q: "Are you in school? Would you like to further your education?", key: "learning" },
          { title: "Housing", q: "Are you satisfied with your living situation?", key: "housing" },
          { title: "Healthy Lifestyle", q: "Diet, exercise, sleep, behaviors?", key: "healthyLifestyle" },
          { title: "Relationships", q: "Important people, family, romance?", key: "relationships" },
          { title: "Parenting", q: "Do you have kids? Co-parenting issues?", key: "parenting" },
          { title: "Community Involvement", q: "Hobbies, interests, volunteering?", key: "activities" }
        ].map((sec) => {
          const areaData = getArea(sec.key);
          return (
            <div className="section" key={sec.key}>
              <p className="section-title">{sec.title}</p>
              <p className="question">{sec.q}</p>
              <div className="answer-text">{areaData.text || "N/A"}</div>
              {areaData.want_to_work && (
                <div className="work-row">
                  <span>Want to work on this now?</span>
                  <span>[ {areaData.want_to_work === "Yes" ? "✓" : " "} ] Yes</span>
                  <span>[ {areaData.want_to_work !== "Yes" ? "✓" : " "} ] No/Maybe later</span>
                </div>
              )}
            </div>
          );
        })}

        <div style={{ pageBreakBefore: "always" }}></div>

        <h3>Part 2. Setting a Preliminary Goal</h3>
        <div className="section">
          <p><strong>I would like to establish a life role goal related to:</strong></p>
          {d.part2_goals?.map((g, i) => <p key={i}>{i + 1}. {g.text}</p>)}
        </div>

        {d.part2_sections?.map((sec) => (
          <div className="section" key={sec.id}>
            <p className="section-title">{sec.section_key.replace(/_/g, ' ')}</p>
            <div className="answer-text">{sec.text}</div>
          </div>
        ))}

        <div className="section">
          <p className="section-title">Practitioner’s Summary</p>
          <div className="answer-text" style={{ minHeight: '150px' }}>{d.part3?.practitioner_summary}</div>
        </div>

        <div className="section">
          <p className="section-title">Practitioner Recommendations</p>
          <div className="answer-text">{d.part3?.practitioner_recommendations}</div>
          <div className="mt-3">
            {d.part3?.checked_items && Object.entries(d.part3.checked_items).map(([key, val]) => (
              <div key={key} style={{ fontSize: '12px' }}>
                [ {val ? "✓" : " "} ] {key.replace(/([A-Z])/g, ' $1').toUpperCase()}
              </div>
            ))}
          </div>
        </div>

        <div className="signature-line">
          <div className="sig-box">Participant Signature</div>
          <div className="sig-box">Date</div>
        </div>

        <div className="signature-line" style={{ marginTop: "40px" }}>
          <div className="sig-box">Staff Signature</div>
          <div className="sig-box">Date</div>
        </div>

        <div className="footer-brand">
          <p>156 Bangalore Town, Tipu Sultan Road Karachi | Ph: 021-34546364-66</p>
          <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
        </div>
      </div>
    </>
  );
}